import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { resolveEffectiveViewId } from "../utils/viewSelection";

const initialValue = [
  {
    id: "EMPTY_COLLECTION",
    views: [], // Consistent empty state
  },
];

export const DataContext = createContext(null);

// Custom hook to use this context
export function useDataContext() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useDataContext must be used within a DataContextProvider");
  }
  return context;
}

export default function DataContextProvider({ children }) {
  // fullData: Array of all collections (used for initial setup in CollectionLoader)
  // Currently only stores the single collection being edited, but kept as array for consistency
  const [fullData, setFullData] = useState(initialValue);
  // currCollData: The currently active collection being edited in the map editor
  const [currCollData, setCurrCollData] = useState(null);
  // activeViewId: Which view in currCollData.views is currently active on the map
  const [activeViewId, setActiveViewId] = useState(null);
  // projectOrgType: Organization type of the project this collection belongs to
  const [projectOrgType, setProjectOrgType] = useState(null);
  const [collectionEntitiesHydratedId, setCollectionEntitiesHydratedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  // Normalizes collection data to ensure consistent structure
  // Used by CollectionLoader to format collection before storing in context
  const normalizeCollections = (collections) => {
    if (!collections || collections.length === 0) return [];
    
    return collections.map(collection => {
      const cleanCollection = {
        id: collection.id,
        key: collection.id,
        projectId: collection.projectId,
        name: collection.name,
        desc: collection.desc,
        status: collection.status,
        updateLog: collection.updateLog,
        tags: collection.tags,
        visibility: collection.visibility,
        ownerId: collection.ownerId,
        accessMap: collection.accessMap,
        defaultViewId: collection.defaultViewId || null,
        currentUserActiveViewId: collection.currentUserActiveViewId || null,
        views: [], // Start with empty views
      };

      if (collection.views && Array.isArray(collection.views)) {
        cleanCollection.views = collection.views.map(v => ({
          id: v.id,
          viewName: v.viewName,
          desc: v.desc,
          center: v.center,
          zoom: v.zoom,
          mapTheme: v.mapTheme,
        }));
      }
      return cleanCollection;
    });
  }

  const updateRecord = useCallback(
    (newRecord) => {
      setFullData((prev) =>
        prev.map((item) => (item.key === newRecord.key ? newRecord : item))
      );
    },
    [setFullData]
  );

  const updateCurrCollData = useCallback(
    (collData, options = {}) => {
      if (!collData) return;

      const { fallbackViewId = activeViewId } = options;

      // Deep clone to avoid reference issues
      const collDataCopy = JSON.parse(JSON.stringify(collData));

      setCurrCollData(collDataCopy);

      // Resolve the effective view using personal active view, then default view,
      // then a safe fallback so refresh/load behavior stays consistent.
      const resolvedViewId = resolveEffectiveViewId(collDataCopy, fallbackViewId);
      setActiveViewId(resolvedViewId);

      // Also update this record in the full data array
      if (collDataCopy.key) {
        updateRecord(collDataCopy);
      }
    },
    [activeViewId, updateRecord]
  );


  // <-- Add function to update project org type -->
  const updateProjectOrgType = useCallback((orgType) => {
    setProjectOrgType(orgType);
  }, []);

  // Collection focus and data loading are handled by CollectionLoader in MapEditor.js
  useEffect(() => {
    setLoading(false);
  }, [location.search]);

  const contextValue = useMemo(
    () => ({
      fullData,
      currCollData,
      activeViewId,
      setActiveViewId,
      updateFullData: normalizeCollections,
      updateRecord,
      updateCurrCollData,
      projectOrgType,
      updateProjectOrgType,
      collectionEntitiesHydratedId,
      setCollectionEntitiesHydratedId,
      loading,
      error,
    }),
    [
      fullData,
      currCollData,
      updateRecord,
      updateCurrCollData,
      activeViewId,
      projectOrgType,
      updateProjectOrgType,
      collectionEntitiesHydratedId,
      loading,
      error,
    ]
  );

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
} 